import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { useMutation } from '@tanstack/react-query'

import styles from './AddEnterpriseDomainDialog.module.scss'

import { isApiError } from '@/api'
import { addEnterpriseDomain } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormField from '@/components/FormField/FormField'
import { useToast } from '@/components/Toast/ToastContext'

const domainSchema = z.object({
  domain: z
    .string()
    .min(1, 'Domain is required')
})

type DomainFormValues = z.infer<typeof domainSchema>

interface AddEnterpriseDomainDialogProps {
  isOpen: boolean
  enterpriseId: string
  enterpriseName: string
  onSave: (workos_synced: boolean) => void
  onCancel: () => void
}

const AddEnterpriseDomainDialog = ({
  isOpen,
  enterpriseId,
  enterpriseName,
  onSave,
  onCancel,
}: AddEnterpriseDomainDialogProps) => {
  const { updateToast } = useToast()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DomainFormValues>({
    resolver: zodResolver(domainSchema),
    defaultValues: { domain: '' },
  })

  const mutation = useMutation({
    mutationFn: (domain: string) => addEnterpriseDomain(enterpriseId, domain),
    onSuccess: (response) => {
      reset()
      onSave(response.data.workos_synced ?? true)
    },
    onError: (err) => {
      console.error('Failed to add domain:', err)
      if (isApiError(err)) {
        updateToast(err.detail || 'Failed to add domain', 'error')
      } else {
        updateToast('Failed to add domain', 'error')
      }
    },
  })

  const handleCancel = () => {
    mutation.reset()
    reset()
    onCancel()
  }

  const onSubmit = (values: DomainFormValues) => {
    mutation.mutate(values.domain)
  }

  return (
    <DialogWrapper isOpen={isOpen} title={`Add Domain to ${enterpriseName}`} close={handleCancel} minWidth={400}>
      <form className={styles.dialogBody} onSubmit={handleSubmit(onSubmit)}>
        <FormField
          name="domain"
          label="Domain"
          control={control}
          error={errors.domain}
          placeholder="example.com"
        />

        <div className={styles.dialogFooter}>
          <Button title="Cancel" onClick={handleCancel} type="light" />
          <Button
            title={mutation.isPending ? 'Adding...' : 'Add Domain'}
            onClick={handleSubmit(onSubmit)}
            type="dark"
            isDisabled={mutation.isPending}
          />
        </div>
      </form>
    </DialogWrapper>
  )
}

export default AddEnterpriseDomainDialog
